import { FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./EidCollectionPage.css";
import { useI18n } from "../../languages/providers/I18nProvider";
import { useLangPath } from "../../hooks/useLangPath";
import grain from "../../components/EidHero/grain.png";
import EidConfetti from "../../components/EidHero/EidConfetti";
import GiftBoxCard from "../../components/giftBoxCard/GiftBoxCard";
import { EID55_BOXES_RESOLVED as BOXES } from "../../data/eid55GiftBoxes";
import { EID55_SCARVES as SCARVES } from "../../data/eid55Scarves";
import WhatsAppFab from "../../components/common/WhatsAppFab";

/**
 * Our Work / Eid Al Etihad 55 — collection + customize page.
 * Assets: /public/eid55/ (logo_v.png, n55.png, grain.png, confetti-2.png, confetti-4.png, palm.png, dhow.png, flag_bearer.png,
 *         flagpole.png, house.png, car_flag.png), /public/eid55/scarves/scarf-1..9.png and gift box images listed in src/data/eid55GiftBoxes.ts
 */

const WA_PHONE = "971554071164";
const BASE = "/eid55";

type L = "en" | "ar";

const T = {
  en: {
    crHome: "Home",
    crWork: "Our Work",
    crHere: "Eid Al Etihad 55",
    heroEyebrow: "Collection · Dec 02, 2026",
    heroT1: "Eid Al Etihad",
    heroT2: "55 collection",
    heroLede:
      "Scarves, flags and celebration prints made in the official Eid Al Etihad 55 identity. Pick a design, tell us the quantity, and we produce it in Abu Dhabi.",
    ctaCustom: "Customize your order",
    ctaWa: "Ask on WhatsApp",
    colEyebrow: "Scarf collection",
    colTitle: "Nine designs, seven emirates",
    colLede:
      "Each scarf uses the official 2026 illustrations and the seven emirate colors. Choose one to customize it.",
    choose: "Customize",
    cuEyebrow: "Customize",
    cuTitle: "Build your order",
    cuLede:
      "Fill in what you know. The form opens WhatsApp with your order written out, and our team replies with a proof and price.",
    fDesign: "Design",
    fQty: "Quantity (pieces)",
    fDate: "Needed by",
    fBrand: "Personalization",
    bNone: "Design as is",
    bLogo: "Add our logo",
    bName: "Add a name or text",
    bHint:
      "Logos are added only in the guideline's approved co-branding layout, next to the Eid Al Etihad logo, never inside it.",
    fName: "Organization",
    fPerson: "Your name",
    fNotes: "Notes",
    fSend: "Send order on WhatsApp",
    fHint: "Nothing is charged until you approve the proof.",
    errQty: "Enter how many pieces you need.",
    prEyebrow: "Also for Eid Al Etihad",
    prTitle: "Flags and prints",
    prLede:
      "The same identity carried across everything you need for the celebration.",
    products: [
      ["National flags", "Hand-held to mega flown flags, in every size."],
      [
        "Street & pole flags",
        "Double-sided pole banners for roads and campuses.",
      ],
      [
        "Building wraps",
        "Large-format fabric printing for facades and fences.",
      ],
      [
        "Banners & stickers",
        "Large-format banners and printed stickers in the Eid identity.",
      ],
    ],
    psEyebrow: "How it works",
    psTitle: "From design to delivery",
    psLede:
      "Approved standard orders are ready within 24 hours, 7 days a week.",
    steps: [
      [
        "Pick a design",
        "Choose one of the nine scarves or ask for a flag or print.",
      ],
      [
        "Send the details",
        "Quantity, date and any logo or text, through the form.",
      ],
      [
        "Approve the proof",
        "We send a digital proof following the official guideline.",
      ],
      ["Production", "Printed in our Abu Dhabi factory and delivered."],
    ],
    legal:
      "Designs follow the official Eid Al Etihad 55 brand guideline. The Eid Al Etihad logo and illustrations belong to their owners and are used as the guideline permits.",
    boxEyebrow: "Gift boxes",
    boxTitle: "A box for every design",
    boxLede:
      "Each box matches one of the nine designs and holds a folded scarf or a flag, a printed mug and a UAE flag pin. You choose what goes inside.",
    boxInside: "Inside the box",
    boxScarf: "Scarf",
    boxFlag: "Flag",
    boxItems: ["Mug in the same design", "UAE flag pin"],
    boxChoose: "Order this box",
    fProduct: "Product",
    pScarf: "Scarf only",
    pBox: "Gift box",
    fInside: "Box contains",
    msgProduct: "Product",
    msgInside: "Box contains",
    msgHiBox:
      "Hello Creation Arts, I'd like to order Eid Al Etihad 55 gift boxes.",
    msgHi: "Hello Creation Arts, I'd like to order Eid Al Etihad 55 scarves.",
    msgDesign: "Design",
    msgQty: "Quantity",
    msgDate: "Needed by",
    msgBrand: "Personalization",
    msgOrg: "Organization",
    msgName: "Name",
    msgNotes: "Notes",
    waAsk:
      "Hello Creation Arts, I have a question about the Eid Al Etihad 55 collection.",
  },
  ar: {
    crHome: "الرئيسية",
    crWork: "أعمالنا",
    crHere: "عيد الاتحاد 55",
    heroEyebrow: "تشكيلة · 2 ديسمبر 2026",
    heroT1: "تشكيلة",
    heroT2: "عيد الاتحاد 55",
    heroLede:
      "أوشحة وأعلام ومطبوعات احتفالية بالهوية الرسمية لعيد الاتحاد الـ55. اختر التصميم وحدد الكمية، ونُنتجها في أبوظبي.",
    ctaCustom: "خصّص طلبك",
    ctaWa: "اسألنا على واتساب",
    colEyebrow: "تشكيلة الأوشحة",
    colTitle: "تسعة تصاميم، سبع إمارات",
    colLede:
      "كل وشاح يستخدم رسومات 2026 الرسمية وألوان الإمارات السبع. اختر واحداً لتخصيصه.",
    choose: "خصّص",
    cuEyebrow: "التخصيص",
    cuTitle: "جهّز طلبك",
    cuLede:
      "املأ ما تعرفه. النموذج يفتح واتساب برسالة طلب جاهزة، ويرد فريقنا بنموذج تصميم وسعر.",
    fDesign: "التصميم",
    fQty: "الكمية (قطعة)",
    fDate: "مطلوب قبل",
    fBrand: "التخصيص",
    bNone: "التصميم كما هو",
    bLogo: "إضافة شعارنا",
    bName: "إضافة اسم أو نص",
    bHint:
      "يُضاف الشعار فقط بتركيب الشراكة المعتمد في الدليل، بجانب شعار عيد الاتحاد وليس داخله.",
    fName: "الجهة",
    fPerson: "اسمك",
    fNotes: "ملاحظات",
    fSend: "أرسل الطلب عبر واتساب",
    fHint: "لا يوجد أي دفع قبل اعتمادك لنموذج التصميم.",
    errQty: "أدخل عدد القطع المطلوبة.",
    prEyebrow: "أيضاً لعيد الاتحاد",
    prTitle: "أعلام ومطبوعات",
    prLede: "نفس الهوية على كل ما تحتاجه للاحتفال.",
    products: [
      [
        "أعلام الدولة",
        "من الأعلام اليدوية إلى الأعلام العملاقة، بكل المقاسات.",
      ],
      ["أعلام الشوارع والأعمدة", "لافتات أعمدة بوجهين للطرق والمباني."],
      ["تغليف المباني", "طباعة قماش بمقاسات كبيرة للواجهات والأسوار."],
      [
        "البنرات والملصقات",
        "بنرات بمقاسات كبيرة وملصقات مطبوعة بهوية عيد الاتحاد.",
      ],
    ],
    psEyebrow: "آلية العمل",
    psTitle: "من التصميم إلى التسليم",
    psLede: "الطلبات القياسية المعتمدة جاهزة خلال 24 ساعة، طوال أيام الأسبوع.",
    steps: [
      ["اختر التصميم", "واحد من الأوشحة التسعة، أو اطلب علماً أو مطبوعة."],
      ["أرسل التفاصيل", "الكمية والتاريخ وأي شعار أو نص، عبر النموذج."],
      ["اعتمد النموذج", "نرسل لك نموذجاً رقمياً وفق الدليل الرسمي."],
      ["الإنتاج", "نطبعه في مصنعنا في أبوظبي ونسلّمه."],
    ],
    legal:
      "التصاميم وفق دليل الهوية الرسمي لعيد الاتحاد الـ55. شعار عيد الاتحاد ورسوماته ملك لأصحابها وتُستخدم وفق ما يسمح به الدليل.",
    boxEyebrow: "صناديق الهدايا",
    boxTitle: "صندوق لكل تصميم",
    boxLede:
      "كل صندوق يطابق أحد التصاميم التسعة، وفيه وشاح أو علم مطوي، وكوب مطبوع، ودبوس علم الإمارات. أنت تختار ما بداخله.",
    boxInside: "داخل الصندوق",
    boxScarf: "وشاح",
    boxFlag: "علم",
    boxItems: ["كوب بنفس التصميم", "دبوس علم الإمارات"],
    boxChoose: "اطلب هذا الصندوق",
    fProduct: "المنتج",
    pScarf: "وشاح فقط",
    pBox: "صندوق هدية",
    fInside: "محتوى الصندوق",
    msgProduct: "المنتج",
    msgInside: "محتوى الصندوق",
    msgHiBox: "مرحباً فنون الإبداع، أرغب بطلب صناديق هدايا عيد الاتحاد الـ55.",
    msgHi: "مرحباً فنون الإبداع، أرغب بطلب أوشحة عيد الاتحاد الـ55.",
    msgDesign: "التصميم",
    msgQty: "الكمية",
    msgDate: "مطلوب قبل",
    msgBrand: "التخصيص",
    msgOrg: "الجهة",
    msgName: "الاسم",
    msgNotes: "ملاحظات",
    waAsk: "مرحباً فنون الإبداع، عندي استفسار عن تشكيلة عيد الاتحاد الـ55.",
  },
} as const;

const PRODUCT_IMG = ["flag_bearer", "flagpole", "house", "gate"];
const pad = (n: number) => String(n).padStart(2, "0");
const wa = (text: string) =>
  `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(text)}`;

type Brand = "none" | "logo" | "name";
type Product = "scarf" | "box";
type Inside = "scarf" | "flag";

export default function EidCollectionPage() {
  const { lang, t } = useI18n();
  const lp = useLangPath();
  const l: L = lang === "ar" ? "ar" : "en";
  const dict = T[l];

  const [sel, setSel] = useState(0);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState("");
  const [brand, setBrand] = useState<Brand>("none");
  const [org, setOrg] = useState("");
  const [person, setPerson] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [product, setProduct] = useState<Product>("scarf");
  const [inside, setInside] = useState<Inside>("scarf");
  const [boxSel, setBoxSel] = useState(0);
  const qtyRef = useRef<HTMLInputElement>(null);
  const customizeRef = useRef<HTMLElement>(null);

  const scarfName = (i: number) => SCARVES[i][l][0];

  const pick = (i: number, p: Product = "scarf") => {
    if (p === "box") {
      setBoxSel(i);
      setSel(BOXES[i].design - 1);
    } else {
      setSel(i);
    }
    setProduct(p);
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    customizeRef.current?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!qty.trim() || Number(qty) < 1) {
      setErr(dict.errQty);
      qtyRef.current?.focus();
      return;
    }
    setErr("");
    const brandTxt =
      brand === "logo"
        ? dict.bLogo
        : brand === "name"
          ? dict.bName
          : dict.bNone;
    const isBox = product === "box";
    const lines = [isBox ? dict.msgHiBox : dict.msgHi, ""];
    lines.push(`${dict.msgProduct}: ${isBox ? dict.pBox : dict.pScarf}`);
    if (isBox)
      lines.push(
        `${dict.msgInside}: ${inside === "flag" ? dict.boxFlag : dict.boxScarf} + ${dict.boxItems.join(" + ")}`,
      );
    lines.push(
      isBox
        ? `${dict.msgDesign}: ${dict.pBox} ${pad(boxSel + 1)} · ${BOXES[boxSel].title[l]}`
        : `${dict.msgDesign}: ${pad(sel + 1)} · ${scarfName(sel)}`,
      `${dict.msgQty}: ${qty.trim()}`,
    );
    if (date) lines.push(`${dict.msgDate}: ${date}`);
    lines.push(`${dict.msgBrand}: ${brandTxt}`);
    if (org.trim()) lines.push(`${dict.msgOrg}: ${org.trim()}`);
    if (person.trim()) lines.push(`${dict.msgName}: ${person.trim()}`);
    if (notes.trim()) lines.push(`${dict.msgNotes}: ${notes.trim()}`);
    window.open(wa(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="eidc"
      dir={l === "ar" ? "rtl" : "ltr"}
      lang={l}
      style={{ ["--grain" as string]: `url(${grain})` }}
    >
      {/* HERO */}
      <header className="hero">
        <div className="in">
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to={lp("/")}>{dict.crHome}</Link>
            <span>/</span>
            <Link to={lp("/our-work")}>{dict.crWork}</Link>
            <span>/</span>
            <b>{dict.crHere}</b>
          </nav>
          <div className="hero-copy">
            <p className="eyebrow">{dict.heroEyebrow}</p>
            <h1 className="disp">
              <span style={{ color: "inherit" }}>{dict.heroT1}</span>
              <span>{dict.heroT2}</span>
            </h1>
            <p className="lede">{dict.heroLede}</p>
            <div className="ctas">
              <a
                className="btn solid"
                href="#customize"
                onClick={(e) => {
                  e.preventDefault();
                  pick(sel);
                }}
              >
                {dict.ctaCustom}
              </a>
              <a
                className="btn"
                href={wa(dict.waAsk)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {dict.ctaWa}
              </a>
            </div>
          </div>
          <div className="hero-art" aria-hidden="true">
            <div className="plate" />
            <img
              className="il"
              src={`${BASE}/palm.png`}
              alt=""
              style={{ left: "-2%", top: "-2%", width: "17%" }}
            />
            <img
              className="il"
              src={`${BASE}/dhow.png`}
              alt=""
              style={{ right: "-4%", top: "58%", width: "20%" }}
            />
            <img
              className="scarf s-a"
              src={`${BASE}/scarves/scarf-6.png`}
              alt=""
            />
            <img
              className="scarf s-b"
              src={`${BASE}/scarves/scarf-3.png`}
              alt=""
            />
            <EidConfetti
              seed={11}
              count={30}
              className="confetti confetti--top"
            />
            <EidConfetti
              seed={37}
              count={24}
              className="confetti confetti--bottom"
            />
            <div className="logo">
              <img src={`${BASE}/logo_v.png`} alt="" />
            </div>
            <img className="n55" src={`${BASE}/n55.png`} alt="" />
          </div>
        </div>
      </header>

      {/* COLLECTION */}
      <section className="band" id="collection">
        <div className="in">
          <div className="sec-head">
            <div>
              <p className="eyebrow">{dict.colEyebrow}</p>
              <h2 className="disp">{dict.colTitle}</h2>
            </div>
            <p>{dict.colLede}</p>
          </div>
          <div className="grid">
            {SCARVES.map((s, i) => (
              <article
                key={i}
                className="card"
                style={{
                  ["--tint" as string]: s.tint,
                  ["--dot" as string]: s.dot,
                }}
              >
                <div className="stage">
                  <span className="num">{pad(i + 1)}</span>
                  <img
                    src={`${BASE}/scarves/scarf-${i + 1}.png`}
                    alt={s[l][0]}
                    loading="lazy"
                  />
                </div>
                <div className="meta">
                  <h3>{s[l][0]}</h3>
                  <p className="sub">{s[l][1]}</p>
                  <div className="row">
                    <span className="swatch">
                      <i />#{pad(i + 1)}
                    </span>
                    <button
                      className="pick"
                      type="button"
                      onClick={() => pick(i)}
                    >
                      {dict.choose}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* GIFT BOXES */}
      <section className="band boxes" id="gift-boxes">
        <div className="in">
          <div className="sec-head">
            <div>
              <p className="eyebrow">{dict.boxEyebrow}</p>
              <h2 className="disp">{dict.boxTitle}</h2>
            </div>
            <p>{dict.boxLede}</p>
          </div>
          <div className="inside-bar">
            <span className="lbl">{dict.boxInside}</span>
            <div
              className="choices"
              role="radiogroup"
              aria-label={dict.boxInside}
            >
              {(["scarf", "flag"] as Inside[]).map((v) => (
                <label key={v} className="chip">
                  <input
                    type="radio"
                    name="eidc-inside-top"
                    id={`eidc-inside-top-${v}`}
                    value={v}
                    checked={inside === v}
                    onChange={() => setInside(v)}
                  />
                  <span>{v === "flag" ? dict.boxFlag : dict.boxScarf}</span>
                </label>
              ))}
            </div>
            <span className="items">+ {dict.boxItems.join(" + ")}</span>
          </div>
          <div className="box-grid">
            {BOXES.map((bx, i) => (
              <GiftBoxCard
                key={bx.id}
                number={i + 1}
                image={bx.image}
                color={bx.color}
                accent={bx.accent}
                title={bx.title[l]}
                description={
                  bx.description?.[l] ||
                  `${inside === "flag" ? dict.boxFlag : dict.boxScarf} + ${dict.boxItems.join(" + ")}`
                }
                alt={`${dict.pBox} · ${bx.title[l]}`}
                ctaLabel={dict.boxChoose}
                selected={product === "box" && boxSel === i}
                onSelect={() => pick(i, "box")}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMIZE */}
      <section className="band custom" id="customize" ref={customizeRef}>
        <div className="in">
          <div
            className={`preview${product === "box" ? " is-box" : ""}`}
            aria-live="polite"
            style={{
              ["--tint" as string]:
                product === "box" ? BOXES[boxSel].color : SCARVES[sel].tint,
            }}
          >
            {product === "box" ? (
              <img
                className="box-big"
                src={BOXES[boxSel].image}
                alt={`${dict.pBox} · ${BOXES[boxSel].title[l]}`}
              />
            ) : (
              <img
                className="big"
                src={`${BASE}/scarves/scarf-${sel + 1}.png`}
                alt={scarfName(sel)}
              />
            )}
            <div className="tag">
              {product === "box"
                ? `${dict.pBox} ${pad(boxSel + 1)} · ${BOXES[boxSel].title[l]}`
                : `${pad(sel + 1)} · ${scarfName(sel)}`}
            </div>
          </div>
          <div>
            <div
              className="sec-head"
              style={{ gridTemplateColumns: "1fr", marginBottom: 24 }}
            >
              <div>
                <p className="eyebrow">{dict.cuEyebrow}</p>
                <h2 className="disp">{dict.cuTitle}</h2>
              </div>
              <p style={{ justifySelf: "start" }}>{dict.cuLede}</p>
            </div>
            <form onSubmit={onSubmit} noValidate>
              <div className="field">
                <span className="lbl">{dict.fProduct}</span>
                <div
                  className="choices"
                  role="radiogroup"
                  aria-label={dict.fProduct}
                >
                  {(["scarf", "box"] as Product[]).map((v) => (
                    <label key={v} className="chip">
                      <input
                        type="radio"
                        name="eidc-product"
                        id={`eidc-product-${v}`}
                        value={v}
                        checked={product === v}
                        onChange={() => setProduct(v)}
                      />
                      <span>{v === "box" ? dict.pBox : dict.pScarf}</span>
                    </label>
                  ))}
                </div>
              </div>
              {product === "box" && (
                <div className="field">
                  <span className="lbl">{dict.fInside}</span>
                  <div
                    className="choices"
                    role="radiogroup"
                    aria-label={dict.fInside}
                  >
                    {(["scarf", "flag"] as Inside[]).map((v) => (
                      <label key={v} className="chip">
                        <input
                          type="radio"
                          name="eidc-inside"
                          id={`eidc-inside-${v}`}
                          value={v}
                          checked={inside === v}
                          onChange={() => setInside(v)}
                        />
                        <span>
                          {v === "flag" ? dict.boxFlag : dict.boxScarf}
                        </span>
                      </label>
                    ))}
                  </div>
                  <span className="hint">+ {dict.boxItems.join(" + ")}</span>
                </div>
              )}
              <div className="field">
                <span className="lbl">{dict.fDesign}</span>
                <div className="dots" role="group" aria-label={dict.fDesign}>
                  {product === "box"
                    ? BOXES.map((bx, i) => (
                        <button
                          key={bx.id}
                          id={`eidc-boxdot-${i}`}
                          type="button"
                          className="dot"
                          style={{ ["--dot" as string]: bx.accent }}
                          aria-label={`${i + 1} · ${bx.title[l]}`}
                          aria-pressed={i === boxSel}
                          onClick={() => {
                            setBoxSel(i);
                            setSel(bx.design - 1);
                          }}
                        />
                      ))
                    : SCARVES.map((s, i) => (
                        <button
                          key={i}
                          id={`eidc-dot-${i}`}
                          type="button"
                          className="dot"
                          style={{ ["--dot" as string]: s.dot }}
                          aria-label={`${i + 1} · ${s[l][0]}`}
                          aria-pressed={i === sel}
                          onClick={() => setSel(i)}
                        />
                      ))}
                </div>
              </div>
              <div className="f2">
                <label htmlFor="eidc-qty">
                  <span>{dict.fQty}</span>
                  <input
                    id="eidc-qty"
                    ref={qtyRef}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    placeholder="200"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </label>
                <label htmlFor="eidc-date">
                  <span>{dict.fDate}</span>
                  <input
                    id="eidc-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </label>
              </div>
              <div className="field">
                <span className="lbl">{dict.fBrand}</span>
                <div
                  className="choices"
                  role="radiogroup"
                  aria-label={dict.fBrand}
                >
                  {(["none", "logo", "name"] as Brand[]).map((b) => (
                    <label key={b} className="chip">
                      <input
                        type="radio"
                        name="eidc-brand"
                        id={`eidc-br-${b}`}
                        value={b}
                        checked={brand === b}
                        onChange={() => setBrand(b)}
                      />
                      <span>
                        {b === "logo"
                          ? dict.bLogo
                          : b === "name"
                            ? dict.bName
                            : dict.bNone}
                      </span>
                    </label>
                  ))}
                </div>
                <span className="hint">{dict.bHint}</span>
              </div>
              <div className="f2">
                <label htmlFor="eidc-org">
                  <span>{dict.fName}</span>
                  <input
                    id="eidc-org"
                    type="text"
                    autoComplete="organization"
                    value={org}
                    onChange={(e) => setOrg(e.target.value)}
                  />
                </label>
                <label htmlFor="eidc-person">
                  <span>{dict.fPerson}</span>
                  <input
                    id="eidc-person"
                    type="text"
                    autoComplete="name"
                    value={person}
                    onChange={(e) => setPerson(e.target.value)}
                  />
                </label>
              </div>
              <label htmlFor="eidc-notes">
                <span>{dict.fNotes}</span>
                <textarea
                  id="eidc-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
              <p className="err" role="alert">
                {err}
              </p>
              <div className="ctas">
                <button className="btn ink" type="submit">
                  {dict.fSend}
                </button>
              </div>
              <span className="hint">{dict.fHint}</span>
            </form>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="band" id="more">
        <div className="in">
          <div className="sec-head">
            <div>
              <p className="eyebrow">{dict.prEyebrow}</p>
              <h2 className="disp">{dict.prTitle}</h2>
            </div>
            <p>{dict.prLede}</p>
          </div>
          <div className="prods">
            {dict.products.map(([title, desc], i) => (
              <div key={title} className="prod">
                <div className="pic">
                  <img
                    src={`${BASE}/${PRODUCT_IMG[i]}.png`}
                    alt=""
                    loading="lazy"
                  />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="band process">
        <div className="in">
          <div className="sec-head">
            <div>
              <p className="eyebrow">{dict.psEyebrow}</p>
              <h2 className="disp">{dict.psTitle}</h2>
            </div>
            <p>{dict.psLede}</p>
          </div>
          <div className="steps">
            {dict.steps.map(([title, desc], i) => (
              <div key={title} className="step">
                <div className="k">{pad(i + 1)}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="in">
        <p className="note">
          {l === "ar" ? (
            <>
              التصاميم وفق{" "}
              <a
                href="https://www.eidaletihad.ae/"
                target="_blank"
                rel="noopener noreferrer"
              >
                دليل الهوية الرسمي لعيد الاتحاد الـ55
              </a>
              . فنون الإبداع ليست جهة تابعة للجهة المنظمة لعيد الاتحاد. شعار عيد
              الاتحاد ورسوماته ملك لأصحابها.
            </>
          ) : (
            <>
              Designs follow the official{" "}
              <a
                href="https://www.eidaletihad.ae/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Eid Al Etihad 55 brand guidelines
              </a>
              . Creation Arts is not affiliated with the Eid Al Etihad
              organizers. The Eid Al Etihad logo and illustrations belong to
              their owners.
            </>
          )}
        </p>
      </div>

      <WhatsAppFab
        phone="971554071164"
        message={t("contact:messages:whatsapp")}
        ariaLabel={t("common:chatWhatsApp")}
        tooltipText={t("contact:messages:whatsappTooltip")}
        bottom={22}
        left={22}
      />
    </div>
  );
}
